import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  createStripePaymentIntent,
  persistStripePaymentRecordIdFromIntentResponse,
} from "../../../payments/stripePaymentBackend";
import { syncStripePaymentToBackend } from "../../../payments/stripePaymentBackend";

function getPaymentIntentContextFromStoredSlip() {
  try {
    const raw = localStorage.getItem("paymentSlipData");
    if (!raw) return { parcelId: null, currency: "gbp" };
    const slip = JSON.parse(raw);
    const v = slip?.parcelId;
    const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
    const parcelId = Number.isFinite(n) && n > 0 ? n : null;
    const code = slip?.currencyType ?? slip?.currency ?? "GBP";
    const currency =
      typeof code === "string" && code.trim()
        ? code.trim().toLowerCase()
        : "gbp";
    return { parcelId, currency };
  } catch {
    return { parcelId: null, currency: "gbp" };
  }
}

/** One POST per parcelId+currency per session (dedupes React Strict Mode double effects and remounts). */
const paymentIntentPromiseByKey = new Map();

function paymentIntentCacheKey(parcelId, currency) {
  return `${parcelId}:${currency}`;
}

function getOrCreatePaymentIntent(parcelId, currency) {
  const key = paymentIntentCacheKey(parcelId, currency);
  const existing = paymentIntentPromiseByKey.get(key);
  if (existing) return existing;

  const promise = createStripePaymentIntent(parcelId, { currency }).catch(
    (err) => {
      paymentIntentPromiseByKey.delete(key);
      throw err;
    },
  );

  paymentIntentPromiseByKey.set(key, promise);
  return promise;
}

function StripeCheckoutForm({ onSucceeded }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        try {
          await syncStripePaymentToBackend(result.error.payment_intent ?? null, {
            paymentStatusId: 3,
          });
        } catch {
          // ignore: we still navigate to failure page
        }
        navigate("/payment-failed", {
          replace: true,
          state: { paymentIntent: result.error.payment_intent ?? undefined },
        });
        return;
      }

      const status = result.paymentIntent?.status;
      if (status === "succeeded") {
        onSucceeded?.();
        try {
          await syncStripePaymentToBackend(result.paymentIntent ?? null, {
            paymentStatusId: 2,
          });
        } catch {
          // ignore: success page will also attempt to sync
        }
        navigate("/payment-success", {
          replace: true,
          state: { paymentIntent: result.paymentIntent },
        });
        return;
      }

      try {
        await syncStripePaymentToBackend(result.paymentIntent ?? null, {
          paymentStatusId: 3,
        });
      } catch {
        // ignore: failure page will also attempt to sync
      }
      navigate("/payment-failed", {
        replace: true,
        state: { paymentIntent: result.paymentIntent },
      });
    } catch (e) {
      navigate("/payment-failed", { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-semibold mb-4">Online Payment</h2>
      <div className="bg-white rounded-xl shadow p-4">
        <PaymentElement />
        <button
          type="button"
          disabled={!stripe || !elements || isSubmitting}
          onClick={handlePay}
          className="mt-4 w-full bg-orange-500 text-white py-3 rounded-md hover:bg-orange-600 disabled:opacity-60 transition-all"
        >
          {isSubmitting ? "Processing..." : "Pay now"}
        </button>
      </div>
    </div>
  );
}

export default function StripePayment() {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);

  const { parcelId, currency } = useMemo(
    () => getPaymentIntentContextFromStoredSlip(),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Enforce previous booking API flow completion before creating intent
      if (sessionStorage.getItem("bookingApisCompleted") !== "1") {
        toast.error("Please complete booking first.");
        navigate("/payment-failed", { replace: true });
        return;
      }

      if (!parcelId) {
        toast.error("Missing parcelId for payment. Please try again.");
        navigate("/payment-failed", { replace: true });
        return;
      }

      setLoading(true);
      try {
        // If user tapped "Try again" on failed page, it already created a new intent.
        const retryParcelId = sessionStorage.getItem("stripeRetryParcelId");
        const retryClientSecret = sessionStorage.getItem("stripeRetryClientSecret");
        const retryPublishableKey = sessionStorage.getItem("stripeRetryPublishableKey");
        const useRetry =
          retryParcelId &&
          String(retryParcelId) === String(parcelId) &&
          retryClientSecret &&
          retryPublishableKey;

        if (useRetry) {
          sessionStorage.removeItem("stripeRetryParcelId");
          sessionStorage.removeItem("stripeRetryClientSecret");
          sessionStorage.removeItem("stripeRetryPublishableKey");

          sessionStorage.setItem("stripePublishableKey", retryPublishableKey);
          setStripePromise(loadStripe(retryPublishableKey));
          setClientSecret(retryClientSecret);
          return;
        }

        const data = await getOrCreatePaymentIntent(parcelId, currency);
        if (cancelled) return;

        persistStripePaymentRecordIdFromIntentResponse(data);

        const cs = data?.clientSecret;
        const pk = data?.publishableKey;
        if (!cs || !pk) {
          toast.error("Payment configuration is missing. Please try again.");
          navigate("/payment-failed", { replace: true });
          return;
        }

        sessionStorage.setItem("stripePublishableKey", pk);
        setStripePromise(loadStripe(pk));
        setClientSecret(cs);
      } catch (e) {
        toast.error("Failed to start payment. Please try again.");
        navigate("/payment-failed", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [parcelId, currency, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="text-lg font-medium">Preparing secure payment...</div>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripeCheckoutForm
        onSucceeded={() => {
          // Open slip once (also opened on /payment-success for redirect flows)
          sessionStorage.setItem("paymentSlipOpened", "1");
          window.open("/paymentslip", "_blank");
        }}
      />
    </Elements>
  );
}

